"use client"

import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/ui/components/ui/toast"

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 250
const DEFAULT_DURATION = 3000

type ToastVariant = NonNullable<ToastProps["variant"]>

type ToasterToast = Omit<ToastProps, "title"> & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  duration?: number
}

type ToastOptions = {
  id?: string | number
  description?: React.ReactNode
  duration?: number
  action?: ToastActionElement
}

type PromiseToastOptions<T> = {
  loading: React.ReactNode
  success: React.ReactNode | ((value: T) => React.ReactNode)
  error: React.ReactNode | ((error: unknown) => React.ReactNode)
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast> & Pick<ToasterToast, "id">
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const removeTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const durationTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function normalizeId(id?: string | number) {
  return id === undefined ? genId() : String(id)
}

function clearDurationTimeout(toastId: string) {
  const timeout = durationTimeouts.get(toastId)
  if (!timeout) {
    return
  }
  clearTimeout(timeout)
  durationTimeouts.delete(toastId)
}

function clearRemoveTimeout(toastId: string) {
  const timeout = removeTimeouts.get(toastId)
  if (!timeout) {
    return
  }
  clearTimeout(timeout)
  removeTimeouts.delete(toastId)
}

function addToRemoveQueue(toastId: string) {
  clearRemoveTimeout(toastId)

  const timeout = setTimeout(() => {
    removeTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId,
    })
  }, TOAST_REMOVE_DELAY)

  removeTimeouts.set(toastId, timeout)
}

function scheduleDismiss(toastId: string, duration?: number) {
  clearDurationTimeout(toastId)

  if (duration === Infinity) {
    return
  }

  const timeout = setTimeout(() => {
    durationTimeouts.delete(toastId)
    dispatch({
      type: "DISMISS_TOAST",
      toastId,
    })
  }, duration ?? DEFAULT_DURATION)

  durationTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      const existingIndex = state.toasts.findIndex(
        (toast) => toast.id === action.toast.id
      )

      if (existingIndex >= 0) {
        const nextToasts = [...state.toasts]
        nextToasts[existingIndex] = {
          ...nextToasts[existingIndex],
          ...action.toast,
        }
        return {
          ...state,
          toasts: nextToasts,
        }
      }

      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }
    }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === action.toast.id ? { ...toast, ...action.toast } : toast
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        clearDurationTimeout(toastId)
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          clearDurationTimeout(toast.id)
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === toastId || toastId === undefined
            ? {
                ...toast,
                open: false,
              }
            : toast
        ),
      }
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        state.toasts.forEach((toast) => {
          clearDurationTimeout(toast.id)
          clearRemoveTimeout(toast.id)
        })
        return {
          ...state,
          toasts: [],
        }
      }

      clearDurationTimeout(action.toastId)
      clearRemoveTimeout(action.toastId)
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== action.toastId),
      }
  }
}

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

function createToast(
  title: React.ReactNode,
  options: ToastOptions = {},
  variant: ToastVariant = "default"
) {
  const id = normalizeId(options.id)

  dispatch({
    type: "ADD_TOAST",
    toast: {
      id,
      title,
      description: options.description,
      action: options.action,
      variant,
      duration: options.duration,
      open: true,
      onOpenChange: (open) => {
        if (!open) {
          dismissToast(id)
        }
      },
    },
  })

  scheduleDismiss(id, options.duration)

  return {
    id,
    dismiss: () => dismissToast(id),
    update: (
      nextTitle: React.ReactNode,
      nextOptions: ToastOptions = {},
      nextVariant: ToastVariant = variant
    ) => createToast(nextTitle, { ...options, ...nextOptions, id }, nextVariant),
  }
}

function dismissToast(toastId?: string | number) {
  dispatch({
    type: "DISMISS_TOAST",
    toastId: toastId === undefined ? undefined : String(toastId),
  })
}

type ToastFn = ((
  title: React.ReactNode,
  options?: ToastOptions
) => ReturnType<typeof createToast>) & {
  success: (title: React.ReactNode, options?: ToastOptions) => ReturnType<typeof createToast>
  error: (title: React.ReactNode, options?: ToastOptions) => ReturnType<typeof createToast>
  warning: (title: React.ReactNode, options?: ToastOptions) => ReturnType<typeof createToast>
  info: (title: React.ReactNode, options?: ToastOptions) => ReturnType<typeof createToast>
  loading: (title: React.ReactNode, options?: ToastOptions) => ReturnType<typeof createToast>
  dismiss: (toastId?: string | number) => void
  promise: <T>(promise: Promise<T>, options: PromiseToastOptions<T>) => Promise<T>
}

const toast = ((title: React.ReactNode, options?: ToastOptions) =>
  createToast(title, options, "default")) as ToastFn

toast.success = (title, options) => createToast(title, options, "success")
toast.error = (title, options) => createToast(title, options, "destructive")
toast.warning = (title, options) => createToast(title, options, "warning")
toast.info = (title, options) => createToast(title, options, "info")
toast.loading = (title, options) =>
  createToast(title, { duration: Infinity, ...options }, "loading")
toast.dismiss = dismissToast
toast.promise = async <T,>(promise: Promise<T>, options: PromiseToastOptions<T>) => {
  const loadingToast = toast.loading(options.loading)

  try {
    const value = await promise
    const successTitle =
      typeof options.success === "function"
        ? options.success(value)
        : options.success

    toast.success(successTitle, { id: loadingToast.id })
    return value
  } catch (error) {
    const errorTitle =
      typeof options.error === "function" ? options.error(error) : options.error

    toast.error(errorTitle, { id: loadingToast.id })
    throw error
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)

    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss: dismissToast,
  }
}

export { useToast, toast }
